import { useCallback, useMemo } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { CONVERSATION_ROUTE_PATH } from '@/entities/conversations';
import { NO_SAMPLE_ROUTE_SEGMENT, SAMPLES_ROUTE_PATH, SAMPLES_ROUTE_PREFIX } from '@/entities/sample';
import { PINNED_PRESET_ROUTE_PATH } from '@/entities/settings';
import { type AppNavigateOptions, appGeneratePath, useAppNavigate } from '@/shared/lib';

/**
 * Адреса раздела, в котором открыт список диалогов: обычного («Диалоги»), закреплённого пресета
 * либо выборок.
 *
 * Раздел определяется адресом, а не настройками: у раздела пресета и у раздела выборок свои
 * сегменты, поэтому все переходы к записи и обратно к списку строятся отсюда — иначе работа
 * с записью выводила бы из своего раздела в общий список диалогов.
 *
 * Раздел выборок распознаётся началом адреса, а не наличием сегмента выборки: список без
 * выбранной выборки — тот же раздел, и запись из него должна открываться в нём же.
 */
export const useDialogsSectionRoute = () => {
  const { slug, sampleId } = useParams<{ slug?: string; sampleId?: string }>();
  const { pathname } = useLocation();
  const navigate = useAppNavigate();

  const isSamplesSection = pathname === SAMPLES_ROUTE_PREFIX || pathname.startsWith(`${SAMPLES_ROUTE_PREFIX}/`);

  /** Адрес списка текущего раздела — куда ведёт возврат из записи */
  const listPath = useMemo(() => {
    if (isSamplesSection) return appGeneratePath(SAMPLES_ROUTE_PATH, { sampleId: sampleId ?? null, id: null });
    if (slug) return appGeneratePath(PINNED_PRESET_ROUTE_PATH, { slug, id: null });
    return appGeneratePath('/dialogs/:id?', { id: null });
  }, [isSamplesSection, sampleId, slug]);

  /** Адрес записи в текущем разделе — для ссылок и открытия в новом окне */
  const buildRecordPath = useCallback(
    (recordId: string) => {
      // Без выбранной выборки её сегмент занимает признак «выборка не выбрана»: иначе адрес
      // записи прочитался бы как адрес выборки с её номером.
      if (isSamplesSection)
        return appGeneratePath(SAMPLES_ROUTE_PATH, { sampleId: sampleId ?? NO_SAMPLE_ROUTE_SEGMENT, id: recordId });
      if (slug) return appGeneratePath(PINNED_PRESET_ROUTE_PATH, { slug, id: recordId });
      return appGeneratePath(CONVERSATION_ROUTE_PATH, { id: recordId });
    },
    [isSamplesSection, sampleId, slug],
  );

  /** Переход к записи текущего раздела; `null` — к списку раздела */
  const navigateToRecord = useCallback(
    (recordId: string | null, options?: AppNavigateOptions) => {
      if (isSamplesSection) {
        const sampleSegment = recordId ? (sampleId ?? NO_SAMPLE_ROUTE_SEGMENT) : (sampleId ?? null);
        navigate(SAMPLES_ROUTE_PATH, { sampleId: sampleSegment, id: recordId }, options);
        return;
      }
      if (slug) {
        navigate(PINNED_PRESET_ROUTE_PATH, { slug, id: recordId }, options);
        return;
      }
      navigate('/dialogs/:id?', { id: recordId }, options);
    },
    [isSamplesSection, sampleId, slug, navigate],
  );

  return { listPath, buildRecordPath, navigateToRecord };
};
